import { TypoTooltip } from "@data-display/wrapper/DetectOverflow";
import { Chip, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { getDate } from "date-fns";
import { type ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CalendarEvent } from "./event";
import type { ICalendarProps } from "./index";
import { classes } from "./styles";
import type { ICalendarCell } from "./types";

export interface ICalendarCellProps<T = undefined> {
  index: number;
  cellPerLine: number;
  cell: ICalendarCell<T>;
  timeZone: string;
  onCellClick?: ICalendarProps<T>["onCellClick"];
  onEventClick?: ICalendarProps<T>["onEventClick"];
  onViewMoreClick?: ICalendarProps<T>["onViewMoreClick"];
}

export const CalendarCell = <T,>({
  index,
  cellPerLine,
  cell,
  timeZone,
  onCellClick,
  onEventClick,
  onViewMoreClick,
}: ICalendarCellProps<T>) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const eventsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [overflowIndex, setOverflowIndex] = useState(-1);

  const placeholders = useMemo(() => {
    const placeholders: ReactNode[] = [];
    for (let i = 0; i < cell.eventsPositionOffset; i++) {
      placeholders.push(<Chip key={i} className={classes.event} size="small" sx={{ visibility: "hidden" }} />);
    }
    return placeholders;
  }, [cell.eventsPositionOffset]);

  const handleResize = useCallback(() => {
    if (container) {
      const computedStyle = getComputedStyle(container);
      for (let i = 0; i < eventsRef.current.length; i++) {
        const element = eventsRef.current[i];
        if (
          element &&
          element.offsetTop + element.offsetHeight >=
            container.clientHeight - Number.parseInt(computedStyle.paddingBottom)
        ) {
          setOverflowIndex(i);
          return;
        }
      }
    }
    setOverflowIndex(-1);
  }, [container]);

  useLayoutEffect(() => {
    if (container) {
      handleResize();
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }
  }, [container, handleResize]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: To run on cell's events update
  useLayoutEffect(() => {
    handleResize();
  }, [cell.events]);

  return (
    <Stack
      className={clsx(classes.cell, { [classes.cellDisabled]: cell.disabled })}
      onDoubleClick={() => onCellClick?.(cell.date)}
      ref={setContainer}
    >
      {index - cellPerLine < 0 && (
        <Typography variant="caption">{cell.date.toLocaleString("fr-FR", { weekday: "short", timeZone })}</Typography>
      )}
      <Typography className={classes.cellDate} variant="caption" onClick={() => onCellClick?.(cell.date)}>
        {getDate(cell.date).toLocaleString("fr-FR", { minimumIntegerDigits: 2 })}
      </Typography>
      <Stack
        sx={{
          alignSelf: "stretch",
          gap: 0.5,
        }}
      >
        {placeholders}
        {cell.events.map((event, eventIndex) => (
          <CalendarEvent
            key={event.id}
            cell={cell}
            event={event}
            overflow={overflowIndex !== -1 && eventIndex >= overflowIndex - 1}
            onEventClick={onEventClick}
            ref={(element) => {
              eventsRef.current[eventIndex] = element;
            }}
          />
        ))}
        <Chip
          className={clsx(classes.event, classes.eventViewMore)}
          size="small"
          label={
            <TypoTooltip variant="body2SemiBold" noWrap>
              +{cell.events.length - overflowIndex + 1} autres
            </TypoTooltip>
          }
          sx={{
            top: eventsRef.current[overflowIndex - 1]?.offsetTop ?? eventsRef.current[overflowIndex]?.offsetTop ?? 0,
            ...(overflowIndex === -1 && { display: "none" }),
          }}
          {...(onViewMoreClick && { onClick: () => onViewMoreClick(cell.events) })}
        />
      </Stack>
    </Stack>
  );
};
